import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import * as React from "react";

import { AppPromotionBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/AppPromotionBanner";
import { CommunityCategoryBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/CommunityCategoryBanner";
import { EcoFriendlyC2PageBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/EcoFriendlyC2PageBanner";
import { FullWidthImageBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/FullWidthImageBanner";
import { HomeFaireMarketBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/HomeFaireMarketBanner";
import { HomeFaireMarketPreshowBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/HomeFaireMarketPreshowBanner";
import { HomeFaireMarketValueProps } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/HomeFaireMarketValueProps";
import { MarketingBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/MarketingBanner";
import { MultiImageTitleBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/MultiImageTitleBanner";
import { MultiUserTeamPermissionEducationBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/MultiUserTeamPermissionEducationBanner";
import { NewBrandsFreeShippingBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/NewBrandsFreeShippingBanner";
import { ShopEuropeBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/ShopEuropeBanner";
import { ShowroomHeaderBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/ShowroomHeaderBanner";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";

import { ShowroomValueTitleBanner } from "./ByType/ShowroomValueTitleBanner";

export const LayoutElementBanner: React.FC = () => {
  const { layoutElement, onVisible, requestId } = React.useContext(Context);
  const viewRef = useDoActionWhenVisible(() => {
    onVisible?.(
      layoutElement.on_click_event_name ??
        getFallbackComponentName(layoutElement.type),
      requestId,
      ""
    );
  });

  if (!layoutElement.type) {
    return null;
  }

  const BannerComponent = bannerComponentMap.get(layoutElement.type);

  if (!BannerComponent) {
    return null;
  }

  return (
    <div ref={viewRef}>
      <BannerComponent />
    </div>
  );
};

export const bannerComponentMap = new Map<
  keyof typeof ILayoutElement.Type | undefined,
  React.FC
>([
  [ILayoutElement.Type.COMMUNITY_CATEGORY_BANNER, CommunityCategoryBanner],
  [ILayoutElement.Type.HOMEPAGE_APP_DOWNLOAD_BANNER, AppPromotionBanner],
  [ILayoutElement.Type.IN_PAGE_IMAGE, HomeFaireMarketBanner],
  [
    ILayoutElement.Type.FAIRE_MARKETS_PRE_SHOW_BANNER,
    HomeFaireMarketPreshowBanner,
  ],
  [ILayoutElement.Type.MAIN_VALUE_PROPS, HomeFaireMarketValueProps],
  [
    ILayoutElement.Type.MID_PRICE_POINT_PLUS_CATEGORY_HOME_PAGE_HEADER_BANNER,
    ShowroomHeaderBanner,
  ],
  [ILayoutElement.Type.SHOP_EUROPE_EDUCATIONAL_BANNER, ShopEuropeBanner],
  [
    ILayoutElement.Type.RETAILER_MULTI_USER_TEAM_EDUCATION_BANNER,
    MultiUserTeamPermissionEducationBanner,
  ],
  [ILayoutElement.Type.FAIRE_MARKETS_HERO_BANNER, HomeFaireMarketBanner],
  [ILayoutElement.Type.FULL_WIDTH_IMAGE_BANNER, FullWidthImageBanner],
  [ILayoutElement.Type.MULTI_IMAGE_TITLE_BANNER, MultiImageTitleBanner],
  [ILayoutElement.Type.MARKETING_BANNER, MarketingBanner],
  [ILayoutElement.Type.ECO_FRIENDLY_DEFINITION_BANNER, EcoFriendlyC2PageBanner],
  [ILayoutElement.Type.SHOWROOM_VALUES_TITLE_BANNER, ShowroomValueTitleBanner],
  [
    ILayoutElement.Type.NEW_BRANDS_FREE_SHIPPING_BANNER,
    NewBrandsFreeShippingBanner,
  ],
]);

const getFallbackComponentName = (
  type: keyof typeof ILayoutElement.Type | undefined
) => {
  if (type !== undefined) {
    return ILayoutElement.Type[type];
  }
  return "";
};
